import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import assert from 'tiny-invariant';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  shareReplay,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import {
  ApiConsumerCallParams,
  ApiConsumerCallParamsWithBody,
  ApiConsumerService,
} from '@ts/shared/api/data-access-api-consumer';
import { BormaDagoApiConsumerService } from '@ts/shared/api/data-access-borma-dago-api-consumer';
import { BormaDagoCsrfStreamService } from '@ts/shared/api/data-access-borma-dago-csrf';
import { BormaDagoHttpErrorResponse } from '@ts/shared/api/util-borma-dago';
import {
  AuthJwtRenewalService,
  AuthJwtTokenStore,
} from '@ts/shared/authentication/data-access';

export const CSRF_OPTIONAL_AUTH_POST_ONLY_ERROR = 'Can only use csrf with POST';

/**
 * Api consumer that always attaches CSRF (like {@link BormaDagoCsrfApiConsumerService})
 * and additionally attaches `Authorization: Bearer <access>` **only when the
 * user is logged in**.
 *
 * Anonymous callers get the request through unchanged (no Authorization
 * header), so anonymous flows like "forgot password" keep working. Logged-in
 * callers get authenticated, so backends that gate behavior on
 * `request.user.is_staff` (e.g. recaptcha bypass via
 * `ValidateRecaptchaTokenIfUserNotStaff`) can see the user.
 *
 * Mirrors {@link BormaDagoAuthenticatedApiConsumerService}'s token-renewal
 * logic: if the call fails with `token_not_valid`, the JWT is refreshed and
 * the call is retried once.
 */
@Injectable({
  providedIn: 'root',
})
export class BormaDagoCsrfOptionalAuthenticatedApiConsumerService implements ApiConsumerService {
  private bormaDagoApiConsumerService = inject(BormaDagoApiConsumerService);
  private bormaDagoCsrfStreamService = inject(BormaDagoCsrfStreamService);
  protected tokenStore = inject(AuthJwtTokenStore);
  protected renewalService = inject(AuthJwtRenewalService);

  csrf$ = this.bormaDagoCsrfStreamService.truthyEntity$.pipe(
    take(1),
    shareReplay(1),
  );

  protected getAuthenticationState$(): AuthJwtTokenStore['state$'] {
    return this.tokenStore.state$.pipe(take(1));
  }

  protected getCallParams$(
    params: ApiConsumerCallParams,
  ): Observable<ApiConsumerCallParams> {
    assert(params.body);
    assert(!('csrf' in params.body));

    return combineLatest([this.csrf$, this.getAuthenticationState$()]).pipe(
      take(1),
      map(([{ csrf }, state]) => {
        const baseHeaders = params.httpOptions?.headers || new HttpHeaders();
        const headers = state
          ? baseHeaders.append('Authorization', `Bearer ${state.access}`)
          : baseHeaders;

        return {
          ...params,
          body: {
            ...params.body,
            csrf,
          },
          httpOptions: {
            ...params.httpOptions,
            withCredentials: true,
            headers,
          },
        };
      }),
    );
  }

  private call$<T>(baseParams: ApiConsumerCallParams): Observable<T> {
    return this.getCallParams$(baseParams).pipe(
      switchMap((params) => {
        return this.bormaDagoApiConsumerService.call$<T>({
          ...params,
          errorCodesToHandleManually: (
            params.errorCodesToHandleManually || []
          ).concat(this.tokenStore.TOKEN_NOT_VALID_ERROR_CODE),
        });
      }),
      catchError((error: BormaDagoHttpErrorResponse) => {
        if (
          error.error.error_code === this.tokenStore.TOKEN_NOT_VALID_ERROR_CODE
        ) {
          return this.renewalService.renew$().pipe(
            switchMap(() => this.getCallParams$(baseParams)),
            switchMap((params) =>
              this.bormaDagoApiConsumerService.call$<T>(params),
            ),
          );
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  get$<T>(): Observable<T> {
    throw new Error(CSRF_OPTIONAL_AUTH_POST_ONLY_ERROR);
  }
  delete$<T>(): Observable<T> {
    throw new Error(CSRF_OPTIONAL_AUTH_POST_ONLY_ERROR);
  }
  post$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'post' });
  }
  put$<T>(): Observable<T> {
    throw new Error(CSRF_OPTIONAL_AUTH_POST_ONLY_ERROR);
  }
  patch$<T>(): Observable<T> {
    throw new Error(CSRF_OPTIONAL_AUTH_POST_ONLY_ERROR);
  }
}
